import { keepPreviousData, useMutation, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query'; //-tech-debt-react-query;

import { apiGet, apiPostForm, apiPostJson } from '../support/aiApi';
import { fetchItems } from '../support/queries';

type QueryOptions<TData> = Omit<
  UseQueryOptions<TData, Error, TData, any>,
  'queryKey' | 'queryFn'
>;

export type LocalUploadTargetType = 'product' | 'go_to_market';

export type NormalizedList<T = any> = T[] & {
  total_count?: number;
  count?: number;
  items?: T[];
  records?: T[];
  results?: T[];
};

export type DocExportLocalUploadStatus =
  | 'pending'
  | 'processing'
  | 'converted'
  | 'failed'
  | 'skipped_duplicate'
  | 'cancelled'
  | string;

export type DocExportLocalUploadRow = {
  id: number;
  upload_id: string;

  target_type?: LocalUploadTargetType | string;

  product?: number | { id?: number; name?: string; [key: string]: any };
  product_id?: number;
  product_name?: string;
  product_offer_type?: string;
  product_recommended_version?: string;

  go_to_market?: number | { id?: number; name?: string; type?: string; [key: string]: any };
  go_to_market_id?: number;
  go_to_market_name?: string;
  go_to_market_type?: string;

  product_version?: string | null;
  title?: string | null;

  original_filename: string;
  original_content_type?: string | null;
  original_size_bytes?: number | null;
  original_sha256?: string | null;

  upload_bucket: string;
  upload_s3_key: string;

  output_bucket?: string | null;
  markdown_s3_key?: string | null;
  manifest_s3_key?: string | null;

  status: DocExportLocalUploadStatus;
  retryable?: boolean;

  error_type?: string | null;
  error_message?: string | null;

  metadata_json?: Record<string, any>;
  conversion_payload?: Record<string, any>;

  uploaded_by?: string | null;
  created?: string;
  started_at?: string | null;
  completed_at?: string | null;
  last_updated?: string;

  is_terminal?: boolean;
  can_process?: boolean;

  s3_paths?: any;
  conversion_summary?: any;

  [key: string]: any;
};

export type LocalFileUploadPayload = {
  target_type: LocalUploadTargetType;
  product_id?: number | null;
  go_to_market_id?: number | null;
  files: File[];
  product_version?: string | null;
};

export type LocalFileProcessPendingPayload = {
  target_type?: LocalUploadTargetType | 'all';
  product_ids?: number[] | null;
  go_to_market_ids?: number[] | null;
  limit?: number;
  include_failed_retryable?: boolean;
  continue_on_error?: boolean;
};

export type LocalFileBatchIngestPayload = {
  product_ids?: number[];
  go_to_market_ids?: number[];
  purpose?: string;
  include_skipped_documents?: boolean;
  chunk_size?: number;
  wait_timeout_seconds?: number;
  poll_seconds?: number;
  overwrite_option?: boolean | null;
};
export type LocalFileCancelUploadPayload = {
  upload_id: string;
  delete_s3_object?: boolean;
};

export const docExportLocalFileKeys = {
  all: ['doc-export-local-files'] as const,

  rows: (params?: Record<string, any>) =>
    ['doc-export-local-files', 'rows', params || {}] as const,

  status: (uploadId?: string | null) =>
    ['doc-export-local-files', 'status', uploadId || null] as const,
};

export const normalizeLocalUploadItems = <T = any>(data: any): T[] => {
  if (!data) return [];
  if (Array.isArray(data)) return data as T[];
  if (Array.isArray(data.items)) return data.items as T[];
  if (Array.isArray(data.records)) return data.records as T[];
  if (Array.isArray(data.results)) return data.results as T[];
  return [];
};

export const normalizeLocalUploadTotalCount = <T = any>(data: any, rows?: T[]): number => {
  const normalizedRows = rows ?? normalizeLocalUploadItems<T>(data);
  if (!data) return normalizedRows.length;
  if (Array.isArray(data)) return data.length;
  return data.total_count ?? data.count ?? normalizedRows.length;
};

export const useFetchDocExportLocalUploads = (
  params: Record<string, any> = {},
  options?: QueryOptions<NormalizedList<DocExportLocalUploadRow>>,
) =>
  useQuery<NormalizedList<DocExportLocalUploadRow>, Error>({
    queryKey: docExportLocalFileKeys.rows(params),
    queryFn: () =>
      fetchItems(
        'db/doc-export-local-upload',
        {
          detailed: true,
          depth: 2,
          size: 25,
          'order-by': '-created',
          ...params,
        },
        {
          pagination: true,
        },
      ) as Promise<NormalizedList<DocExportLocalUploadRow>>,
    ...{
        placeholderData: keepPreviousData,
        ...(options || {}),
      },
  });

export const useFetchDocExportLocalUploadStatus = (
  uploadId?: string | null,
  params: Record<string, any> = {},
  options?: QueryOptions<any>,
) =>
  useQuery<any, Error>({
    queryKey: docExportLocalFileKeys.status(uploadId),
    queryFn: () =>
      apiGet<any>(
        `ai/doc-export/local-files/status/${encodeURIComponent(String(uploadId))}/`,
        params,
      ),
    ...{
        enabled: Boolean(uploadId),
        refetchInterval: 5000,
        ...(options || {}),
      },
  });

export const useUploadDocExportLocalFilesMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<any[], Error, LocalFileUploadPayload>({
    mutationFn: async (payload) => {
      if (!payload.target_type) {
        throw new Error('target_type is required');
      }

      if (payload.target_type === 'product' && !payload.product_id) {
        throw new Error('product_id is required for product uploads');
      }

      if (payload.target_type === 'go_to_market' && !payload.go_to_market_id) {
        throw new Error('go_to_market_id is required for Go-To-Market uploads');
      }

      if (!payload.files?.length) {
        throw new Error('At least one file is required');
      }

      const results: any[] = [];

      for (const file of payload.files) {
        const form = new FormData();

        form.append('target_type', payload.target_type);
        form.append('file', file);

        if (payload.product_id) {
          form.append('product_id', String(payload.product_id));
        }

        if (payload.go_to_market_id) {
          form.append('go_to_market_id', String(payload.go_to_market_id));
        }

        if (payload.product_version) {
          form.append('product_version', payload.product_version);
        }

        const result = await apiPostForm<any>('ai/doc-export/local-files/upload/', form);
        results.push(result);
      }

      return results;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: docExportLocalFileKeys.all });
      queryClient.invalidateQueries({ queryKey: ['doc-export-local-upload'] });
      queryClient.invalidateQueries({ queryKey: ['rag'] });
    },
  });
};

export const useProcessPendingDocExportLocalFilesMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<any, Error, LocalFileProcessPendingPayload>({
    mutationFn: async (payload) =>
      apiPostJson<any>('ai/doc-export/local-files/process-pending/', {
        target_type: payload.target_type || 'all',
        product_ids: payload.product_ids?.length ? payload.product_ids : null,
        go_to_market_ids: payload.go_to_market_ids?.length ? payload.go_to_market_ids : null,
        limit: payload.limit ?? 100,
        include_failed_retryable: Boolean(payload.include_failed_retryable),
        continue_on_error: payload.continue_on_error !== false,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: docExportLocalFileKeys.all });
      queryClient.invalidateQueries({ queryKey: ['doc-export-local-upload'] });
      queryClient.invalidateQueries({ queryKey: ['rag'] });
    },
  });
};

export const useBatchIngestDocExportLocalTargetsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<any, Error, LocalFileBatchIngestPayload>({
    mutationFn: async (payload) => {
      const productIds = payload.product_ids || [];
      const gtmIds = payload.go_to_market_ids || [];

      if (!productIds.length && !gtmIds.length) {
        throw new Error('At least one product_id or go_to_market_id is required');
      }

      return apiPostJson<any>('ai/doc-export/products/ingest-batch/', {
        product_ids: productIds.length ? productIds : undefined,
        go_to_market_ids: gtmIds.length ? gtmIds : undefined,
        purpose: payload.purpose || 'doc_export',
        include_skipped_documents: payload.include_skipped_documents ?? false,
        chunk_size: payload.chunk_size ?? 20,
        wait_timeout_seconds: payload.wait_timeout_seconds ?? 14400,
        poll_seconds: payload.poll_seconds ?? 15.0,
        overwrite_option: payload.overwrite_option ?? null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rag'] });
      queryClient.invalidateQueries({ queryKey: ['rag-pipeline-ingestion-run'] });
    },
  });
};


export const useCancelDocExportLocalUploadMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<any, Error, LocalFileCancelUploadPayload>({
    mutationFn: async (payload) => {
      if (!payload.upload_id) {
        throw new Error('upload_id is required');
      }

      return apiPostJson<any>(
        `ai/doc-export/local-files/cancel/${encodeURIComponent(payload.upload_id)}/`,
        {
          delete_s3_object: payload.delete_s3_object !== false,
        }
      );
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: docExportLocalFileKeys.all });
      queryClient.invalidateQueries({ queryKey: ['doc-export-local-upload'] });
      queryClient.invalidateQueries({ queryKey: docExportLocalFileKeys.status(variables.upload_id) });
    },
  });
};